import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { resolvePostAuthDestination } from '../../utils/authRedirect';

const GOOGLE_SCRIPT_ID = 'google-identity-service';

const GoogleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const decodeGoogleProfile = (credential) => {
  try {
    const payload = credential.split('.')[1];
    if (!payload) return {};
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
  } catch {
    return {};
  }
};

const GoogleSignInButton = ({ compact = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle } = useAuth();
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const initializedRef = useRef(false);
  const renderedRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [retryKey, setRetryKey] = useState(0);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!googleClientId) {
      setStatus('missing-config');
      return undefined;
    }

    setStatus('loading');
    initializedRef.current = false;
    renderedRef.current = false;
    if (buttonRef.current) buttonRef.current.innerHTML = '';

    const handleCredential = async ({ credential }) => {
      if (!credential) {
        setStatus('error');
        toast.error('Google did not return a credential.');
        return;
      }

      setStatus('connecting');
      try {
        const googleProfile = decodeGoogleProfile(credential);
        const response = await loginWithGoogle(credential);
        if (response?.needsOnboarding) {
          navigate('/auth/onboarding', {
            replace: true,
            state: {
              from: location.state?.from || '/',
              onboardingToken: response.onboardingToken,
              profile: {
                ...(response.profile || {}),
                email: response.profile?.email || googleProfile.email || '',
                name: response.profile?.name || googleProfile.name || '',
                picture: response.profile?.picture || response.profile?.avatarUrl || googleProfile.picture || '',
              },
            },
          });
          return;
        }

        navigate(resolvePostAuthDestination(location.state?.from || '/', response?.user?.role), { replace: true });
      } catch (error) {
        setStatus('ready');
        toast.error(error?.response?.data?.message || 'Google login failed. Please try again.');
      }
    };

    const initializeAndRender = () => {
      if (!window.google?.accounts?.id || !buttonRef.current) return false;

      if (!initializedRef.current) {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleCredential,
        });
        initializedRef.current = true;
      }

      if (!renderedRef.current) {
        const containerWidth = wrapperRef.current?.offsetWidth || 400;
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          shape: 'rectangular',
          text: 'continue_with',
          logo_alignment: 'left',
          width: Math.max(containerWidth, 300),
        });
        renderedRef.current = true;
      }

      setStatus('ready');
      return true;
    };

    if (initializeAndRender()) return undefined;

    let script = document.getElementById(GOOGLE_SCRIPT_ID);
    if (script?.dataset.status === 'failed') {
      script.remove();
      script = null;
    }

    const handleLoad = () => {
      script.dataset.status = 'loaded';
      window.setTimeout(() => {
        if (!initializeAndRender()) setStatus('error');
      }, 50);
    };
    const handleError = () => {
      script.dataset.status = 'failed';
      setStatus('error');
    };

    if (!script) {
      script = document.createElement('script');
      script.id = GOOGLE_SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.dataset.status = 'loading';
      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);
      script.src = 'https://accounts.google.com/gsi/client';
      document.head.appendChild(script);
    } else if (script.dataset.status === 'loaded') {
      window.setTimeout(() => {
        if (!initializeAndRender()) setStatus('error');
      }, 50);
    } else {
      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);
    }

    const poll = window.setInterval(() => {
      if (initializeAndRender()) window.clearInterval(poll);
    }, 250);
    const timeout = window.setTimeout(() => {
      if (!window.google?.accounts?.id || !renderedRef.current) setStatus('error');
    }, 10000);

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      window.clearInterval(poll);
      window.clearTimeout(timeout);
    };
  }, [compact, googleClientId, location.state, loginWithGoogle, navigate, retryKey]);

  if (status === 'missing-config') {
    return (
      <button
        type="button"
        disabled
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          width: '100%', padding: '11px 16px', borderRadius: '8px',
          border: '1px solid var(--gray-5)', backgroundColor: '#fff',
          fontSize: '14px', fontWeight: 500, color: 'var(--gray-9)',
          cursor: 'not-allowed', fontFamily: "'Poppins', sans-serif",
        }}
      >
        <GoogleIcon />
        Google not configured
      </button>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <button
          type="button"
          onClick={() => {
            document.getElementById(GOOGLE_SCRIPT_ID)?.remove();
            setRetryKey((k) => k + 1);
          }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            width: '100%', padding: '11px 16px', borderRadius: '8px',
            border: '1px solid var(--red-6, #fca5a5)', backgroundColor: '#fff',
            fontSize: '14px', fontWeight: 500, color: 'var(--red-9, #e5484d)',
            cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
          }}
        >
          <GoogleIcon />
          Retry Google sign-in
        </button>
        <p style={{ fontSize: '12px', textAlign: 'center', color: 'var(--gray-10)', margin: 0 }}>
          Google sign-in could not load. Check your connection or ad blocker.
        </p>
      </div>
    );
  }

  /* ── Main render: our custom button (visual) + transparent Google button on top ── */
  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        width: '100%',
        height: compact ? '40px' : '44px',
        opacity: status === 'connecting' ? 0.65 : 1,
        pointerEvents: status === 'connecting' ? 'none' : 'auto',
      }}
    >
      {/* Our styled button — purely visual, pointer-events disabled */}
      {compact ? (
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            pointerEvents: 'none',
            fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)',
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          <GoogleIcon size={20} />
          Continue with Google
        </div>
      ) : (
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            borderRadius: '8px', border: '1px solid var(--gray-5)',
            backgroundColor: '#fff',
            fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)',
            fontFamily: "'Poppins', sans-serif", pointerEvents: 'none',
          }}
        >
          <GoogleIcon size={18} />
          {status === 'loading' ? 'Loading…' : 'Continue with Google'}
        </div>
      )}

      {/* Transparent Google button — sits on top, handles all click events */}
      <div
        ref={buttonRef}
        style={{
          position: 'absolute', inset: 0,
          overflow: 'hidden', opacity: 0,
        }}
      />
    </div>
  );
};

export default GoogleSignInButton;
